import { Suspense, lazy, type ReactNode } from "react";
import { Navigate, Route, Routes } from "react-router";

import { AppShell } from "./components/AppShell";
import { LoadingState } from "./components/states";
import { RequireAuth } from "./lib/auth";
import { AcceptInvitePage } from "./pages/AcceptInvitePage";
import { LoginPage } from "./pages/LoginPage";
import { NotFoundPage } from "./pages/NotFoundPage";

// Pages are split so MapLibre (bond detail, AA manager) and Recharts (portfolio, peers) only
// load where they are used; the Screen page stays light.
const ScreenPage = lazy(() => import("./pages/ScreenPage").then((m) => ({ default: m.ScreenPage })));
const BondDetailPage = lazy(() => import("./pages/BondDetailPage").then((m) => ({ default: m.BondDetailPage })));
const MemosPage = lazy(() => import("./pages/MemosPage").then((m) => ({ default: m.MemosPage })));
const PortfolioPage = lazy(() => import("./pages/PortfolioPage").then((m) => ({ default: m.PortfolioPage })));
const PeersPage = lazy(() => import("./pages/PeersPage").then((m) => ({ default: m.PeersPage })));
const AdminPage = lazy(() => import("./pages/admin/AdminPage").then((m) => ({ default: m.AdminPage })));

function Page({ children }: { children: ReactNode }) {
  return <Suspense fallback={<LoadingState label="Loading page…" />}>{children}</Suspense>;
}

export function App() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/accept-invite" element={<AcceptInvitePage />} />
      <Route
        element={
          <RequireAuth>
            <AppShell />
          </RequireAuth>
        }
      >
        <Route index element={<Navigate to="/screen" replace />} />
        <Route path="/screen" element={<Page><ScreenPage /></Page>} />
        <Route path="/bonds/:cusip" element={<Page><BondDetailPage /></Page>} />
        <Route path="/memos" element={<Page><MemosPage /></Page>} />
        <Route path="/portfolio" element={<Page><PortfolioPage /></Page>} />
        <Route path="/peers" element={<Page><PeersPage /></Page>} />
        <Route
          path="/admin/*"
          element={
            <RequireAuth roles={["platform_admin", "tenant_admin"]}>
              <Page>
                <AdminPage />
              </Page>
            </RequireAuth>
          }
        />
        <Route path="*" element={<NotFoundPage />} />
      </Route>
    </Routes>
  );
}
